import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useMap } from '@/hooks/useMap';
import { useMapReadyState } from '@/hooks/use-map-ready-state';
import { MAP_OVERLAY_FADE_IN_MS } from '@/constants/map-constants';
import { createWindStreamletEngine } from '@/components/map/wind-streamlet-engine';
import type { WindStreamletEngine } from '@/components/map/wind-streamlet-engine';
import type { Waypoint } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import type { BlindMetadata } from '@/types/waypoint';
import { isWindFavorable } from '@/utils/wind-favorability';

interface WindStreamletLayerProps {
  /** Meteorological degrees wind is blowing FROM (0=N, 90=E, 180=S, 270=W). */
  windDeg: number | null;
  /** Wind speed in mph. */
  windSpeedMph: number | null;
  /** When false the canvas is cleared and animation is stopped. */
  visible: boolean;
  /** All property locations — used to derive favorable blind positions. */
  locations?: Waypoint[];
  /** Current wind cardinal direction (e.g. "NW") — used for favorability check. */
  currentWindCardinal?: string | null;
}

/**
 * Renders an animated streamlet overlay on a canvas element that sits above
 * the MapLibre map canvas. The canvas is sized to the map container and
 * resized via ResizeObserver.
 *
 * This component owns no business logic — it delegates to
 * `createWindStreamletEngine` (RULE-044).
 *
 * Lifecycle contract:
 *   - Mount effect: creates canvas + engine. Does NOT start animation.
 *   - Visible effect: handles all start/stop transitions (runs on mount + changes).
 *   - Unmount: cleanup in mount effect return.
 */
export function WindStreamletLayer({
  windDeg,
  windSpeedMph,
  visible,
  locations,
  currentWindCardinal,
}: WindStreamletLayerProps) {
  const map = useMap();
  const { isReady } = useMapReadyState(map);
  const engineRef = useRef<WindStreamletEngine | null>(null);

  // Keep a ref to the latest wind props so the engine's `getWind` closure
  // always reads the most recent value without requiring an effect re-run.
  const windRef = useRef<{ degFrom: number; speedMph: number } | null>(null);
  // Compute the next wind value and stage it — committed via useLayoutEffect below.
  const nextWindValue =
    windDeg !== null && windSpeedMph !== null
      ? { degFrom: windDeg, speedMph: windSpeedMph }
      : null;

  // Keep a ref to the latest getFavorableStandPositions closure so the engine
  // always sees the freshest locations/cardinal without re-mounting.
  const getFavorableStandPositionsRef = useRef<() => Array<{ x: number; y: number }>>(() => []);

  // Build the closure and store it via ref so the engine captures latest state.
  // Recomputed each invocation to track map pan/zoom — intentionally not cached.
  const getFavorableStandPositions = useCallback(() => {
    if (!locations || !currentWindCardinal || !map) return [];
    return locations
      .filter((loc): loc is Waypoint & { type: WaypointType.Blind } => loc.type === WaypointType.Blind)
      .filter((loc) => {
        const meta = loc.metadata as BlindMetadata;
        return isWindFavorable(currentWindCardinal, meta.idealWindDirections ?? []) === 'favorable';
      })
      .map((loc) => {
        // Recomputed each invocation to track map pan/zoom — intentionally not cached
        const pt = map.project([loc.longitude, loc.latitude]);
        return { x: pt.x, y: pt.y };
      });
  }, [locations, currentWindCardinal, map]);

  // Update both "latest value" refs synchronously after render, before paint.
  // useLayoutEffect is the correct hook for this pattern — it fires synchronously
  // so the engine closure always sees up-to-date values without an async gap.
  useLayoutEffect(() => {
    windRef.current = nextWindValue;
    getFavorableStandPositionsRef.current = getFavorableStandPositions;
  });

  // ── Mount effect: create canvas + engine only ─────────────────────────
  useEffect(() => {
    const container = map.getContainer();
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.setAttribute('data-testid', 'wind-streamlet-canvas');
    canvas.setAttribute('data-wind-overlay-active', 'false');
    // data-map-overlay-state drives the J-09 ghost-free overlay gate
    canvas.setAttribute('data-map-overlay-state', 'hidden');
    canvas.style.opacity = '0';
    // data-favorability-glow-active tracks whether any blind-type waypoints are present
    const hasBlindLocations = (locations ?? []).some(loc => loc.type === WaypointType.Blind);
    canvas.setAttribute(
      'data-favorability-glow-active',
      hasBlindLocations ? 'true' : 'false',
    );
    container.appendChild(canvas);

    function getViewport() {
      return { width: canvas.width, height: canvas.height };
    }

    function syncSize() {
      const { offsetWidth: w, offsetHeight: h } = container;
      if (w > 0 && h > 0) {
        if (engineRef.current) {
          engineRef.current.resize(w, h);
        } else {
          canvas.width = w;
          canvas.height = h;
        }
      }
    }

    syncSize();

    const resizeObserver = new ResizeObserver(syncSize);
    resizeObserver.observe(container);

    // Also sync when MapLibre fires its own resize event (e.g., fullscreen
    // toggle, container style change) which may not trigger ResizeObserver.
    map.on('resize', syncSize);

    // Mount engine without streamletCount (grid spawn is automatic)
    engineRef.current = createWindStreamletEngine({
      canvas,
      getViewport,
      getWind: () => windRef.current,
      getFavorableStandPositions: () => getFavorableStandPositionsRef.current(),
    });

    // Seed initial bearing in case the map was already rotated before mount.
    engineRef.current.updateBearing(map.getBearing());

    // Track map rotation so streamlets adjust to screen-space wind direction (Fix 3).
    const onRotate = () => {
      engineRef.current?.updateBearing(map.getBearing());
    };
    map.on('rotate', onRotate);

    return () => {
      resizeObserver.disconnect();
      map.off('resize', syncSize);
      map.off('rotate', onRotate);
      const engine = engineRef.current;
      if (engine) {
        engine.stop();
        engineRef.current = null;
      }
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount/unmount only

  // ── Sync data-favorability-glow-active attribute ──────────────────────
  useEffect(() => {
    const container = map.getContainer();
    const canvas = container.querySelector<HTMLCanvasElement>('[data-testid="wind-streamlet-canvas"]');
    if (canvas) {
      const hasBlindLocations = (locations ?? []).some(loc => loc.type === WaypointType.Blind);
      canvas.setAttribute(
        'data-favorability-glow-active',
        hasBlindLocations ? 'true' : 'false',
      );
    }
  }, [locations, map]);

  // ── Visible + ready effect: single source of truth for start/stop/fade ──────
  // Engine starts only when BOTH visible=true AND map isReady (idle).
  // data-map-overlay-state drives CSS opacity transition for ghost-free overlay
  // appearance (J-09). Transitions:
  //   "hidden"  → engine stopped, canvas opacity:0
  //   "fading"  → engine started (so first frame is ready), CSS transitioning opacity 0→1
  //   "ready"   → transition complete, canvas fully opaque
  // The CSS transition duration is MAP_OVERLAY_FADE_IN_MS (180ms).
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    const container = map.getContainer();
    const canvas = container.querySelector<HTMLCanvasElement>('[data-testid="wind-streamlet-canvas"]');
    if (!canvas) return;

    const shouldShow = visible && isReady;

    if (shouldShow) {
      engine.start();
      canvas.setAttribute('data-wind-overlay-active', 'true');
      canvas.setAttribute('data-map-overlay-state', 'ready');
      // Respect prefers-reduced-motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      canvas.style.transition = prefersReducedMotion ? 'none' : `opacity ${MAP_OVERLAY_FADE_IN_MS}ms ease-in`;
      canvas.style.opacity = '1';
    } else {
      engine.stop();
      canvas.setAttribute('data-wind-overlay-active', 'false');
      canvas.setAttribute('data-map-overlay-state', 'hidden');
      // Immediate hide — no fade-out (MAP_OVERLAY_FADE_OUT_MS = 0)
      canvas.style.transition = 'none';
      canvas.style.opacity = '0';
    }
  }, [visible, isReady, map]);

  return null;
}
